// Copyright (c) Zammo Inc.

export type VoiceNamePair = {
  locale: string;
  directLineSpeechVoiceUri: string;
};

export type SimulatorSession = {
  /**
   * @property {string} directLineToken the token generated by the zammo API to authenticate the business with Zammo business.
   */
  directLineToken: string;

  /**
   * @property {string} speechToken the token generated by the zammo API to authenticate the business with direct line speech.
   */
  speechToken: string;

  /**
   * @property {string} speechRegion the region used to authenticate with direct line speech.
   */
  speechRegion: string;

  /**
   * @property {VoiceNamePair[]} voiceNamePairs the voices to use for directline speech.
   */
  speechVoiceNames: VoiceNamePair[];

  /**
   * @property {string} uiKitScriptUrl the URL for UI Kit Script
   */
  uiKitScriptUrl?: string;

  /**
   * @property {string} uiKitStyleUrl the URL for UI Kit CSS
   */
  uiKitStyleUrl?: string;

  customCssContent?: string;

  customJsContent?: string;
};

interface GetSimulatorSessionDto {
  token: string;
  language: string;
  country: string;
  userId?: string;
}

export interface SpeechSession {
  speechToken: string;
  speechRegion: string;
}

export interface UploadResult {
  downloads: ZammoAttachment[];
}

export interface ZammoAttachment {
  contentType: string;
  contentUrl: string;
  contentBase64: string;
  message: string;
  originalFileName: string;
  isValid: boolean;
}

/**
 * Uploads files to the Zammo API for the simulator
 * @param {string} simulatorToken - The simulator token for authentication
 * @param {File[]} files - Array of files to upload
 * @param {string} conversationId - The conversation ID
 * @param {number} expiryMinutes - Minutes until upload link expires
 * @returns {Promise<UploadResult>} The upload result with download URLs
 */
export const uploadFilesForSimulator = (
  simulatorToken: string,
  files: File[],
  conversationId: string,
  expiryMinutes: number
): Promise<UploadResult> => {
  const formData = new FormData();
  files.forEach((file) => {
    formData.append('files', file);
  });

  const params = new URLSearchParams({
    simulatorToken,
    conversationId,
    expiryMinutes: expiryMinutes.toString(),
  });

  return fetch(`${window.ZAMMO_API_URL}/api/simulator/attachment-upload?${params}`, {
    method: 'POST',
    body: formData,
  }).then((response) => {
    if (!response.ok) {
      throw Error(response.statusText);
    }
    return response.json();
  });
};

/**
 * @param {string} simulatorToken the simulatorToken from the frontend.
 * @return {Promise<string>} the directLineToken.
 */
export const getSimulatorSession = (simulatorToken: string, userId?: string): Promise<SimulatorSession> => {
  const reqBody: GetSimulatorSessionDto = { token: simulatorToken, language: 'en', country: 'US' };

  if (userId) {
    reqBody.userId = userId;
  }
  return fetch(`${window.ZAMMO_API_URL}/api/simulator/session`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(reqBody),
  }).then((response) => {
    if (!response.ok) {
      throw Error(response.statusText);
    }
    return response.json();
  });
};

/**
 * @param {string} simulatorToken the simulatorToken from the frontend.
 * @return {Promise<string>} the directLineToken.
 */
export const getSimulatorSpeechSession = (simulatorToken: string): Promise<SpeechSession> => {
  return fetch(`${window.ZAMMO_API_URL}/api/simulator/speech/session?simulatorToken=${simulatorToken}`, {
    method: 'GET',
  }).then((response) => {
    if (!response.ok) {
      throw Error(response.statusText);
    }
    return response.json();
  });
};

/**
 * This function will retrieve the content of the webpage at the specified url.
 * This function is used when the page can't be retrieved in an Iframe.
 * @param {string} urlToFetch the url for which the content will be retrieved.
 * @returns {string} the content of the page.
 */
export const getUrl = (urlToFetch: string): Promise<string> => {
  const url = window.ZAMMO_API_URL;
  return fetch(`${url}/api/simulator/fetch?urlToFetch=${urlToFetch}`).then((res) => res.text());
};

/**
 * @param {string} urlToCheck the url for which the backend will assess if it can be used in an Iframe.
 * @returns {boolean} if the url can be used in an IFrame
 */
export const canUrlCanBeFetchedInIframe = (urlToCheck: string): Promise<boolean> => {
  return fetch(`${window.ZAMMO_API_URL}/api/simulator/canbefetchediniframe?urlToCheck=${urlToCheck}`)
    .then((res) => res.json())
    .then((resJson) => resJson.canBeFetchedInIFrame);
};

export type Voice = {
  Name: string;
  DisplayName: string;
  LocalName: string;
  ShortName: string;
  Gender: string;
  Locale: string;
  StyleList: string;
  SampleRateHertz: string;
  VoiceType: string;
  Status: string;
};

export const getVoiceList = (speechToken: string, speechRegion: string): Promise<Voice[]> => {
  return fetch(`https://${speechRegion}.tts.speech.microsoft.com/cognitiveservices/voices/list`, {
    method: 'GET',
    headers: {
      Accept: 'application/json',
      Authorization: `Bearer ${speechToken}`,
    },
  }).then((res) => res.json());
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const getPersonalizerConfiguration = (simulatorToken: string, locale?: string): Promise<any> => {
  const params = new URLSearchParams({ simulatorToken });
  if (locale) {
    params.append('locale', locale);
  }

  return fetch(`${window.ZAMMO_API_URL}/api/webchat/configuration/simulator?${params}`, {
    method: 'GET',
  }).then((response) => {
    if (!response.ok) {
      throw Error(response.statusText);
    }
    return response.json();
  });
};
